import { OF, type QueryValue } from '../../ofApi'

/*
 * onlyfans.com/my/streaming/dashboard (captured 2026-10-22, bundle
 * streaming-dashboard / streaming-create-edit-schedule / stream stats modal):
 * the requests it makes and the shapes it reads, as pure functions.
 */

type Raw = Record<string, unknown>

const obj = (v: unknown): Raw => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : {})
const arr = (v: unknown): Raw[] => (Array.isArray(v) ? (v.filter((x) => x && typeof x === 'object') as Raw[]) : [])
const str = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '')
const num = (v: unknown): number => (typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)) ? Number(v) : 0)
const path = (v: unknown, ...keys: string[]): unknown => keys.reduce<unknown>((cur, k) => obj(cur)[k], v)

// ── Pages & referers ──────────────────────────────────────────────────────

export const DASHBOARD_REFERER = `${OF}/my/streaming/dashboard`
export const CREATE_REFERER = `${OF}/my/streaming/dashboard/create`
export const editReferer = (id: string) => `${OF}/my/streaming/dashboard/${id}`
/** Where going live happens (OnlyFans' LIVE VIDEO page). */
export const GO_LIVE_URL = `${OF}/my/streaming`
export const OBS_DOWNLOAD_URL = 'https://obsproject.com/download'

/** Our page's query ↔ OnlyFans' routes: dashboard, `dashboard/create`, `dashboard/:id`. */
export type StreamingView = { kind: 'dashboard' } | { kind: 'create'; scheduleDate: string } | { kind: 'edit'; id: string }

export function parseView(query: string): StreamingView {
  const q = new URLSearchParams(query)
  const edit = (q.get('edit') ?? '').trim()
  if (/^\d+$/.test(edit)) return { kind: 'edit', id: edit }
  if (q.get('view') === 'create') return { kind: 'create', scheduleDate: q.get('scheduleDate') ?? '' }
  return { kind: 'dashboard' }
}

// ── Requests (bodies and queries exactly as the bundle builds them) ──────

/** fetchPastStreams: users/:id/streams, 8 a page. */
export const PAST_LIMIT = 8
export const pastStreamsQuery = (offset: number): Record<string, QueryValue> => ({ limit: PAST_LIMIT, offset, skip_users: 'all' })

/** getStreamStats({ id }) with its default withChart=true (countersOnly unset is left out). */
export const statsQuery = (): Record<string, QueryValue> => ({ withChart: true })

/** statistic/fetchStreamViewersStat (8 a page); the tippers list adds onlyWithTips. */
export const VIEWERS_LIMIT = 8
export function viewersQuery(offset: number, tippers = false): Record<string, QueryValue> {
  return tippers
    ? { withFinishedViewers: true, onlyCurrentStreamTips: true, onlyWithTips: true, limit: VIEWERS_LIMIT, offset }
    : { withFinishedViewers: true, onlyCurrentStreamTips: true, limit: VIEWERS_LIMIT, offset }
}

/** ScheduledEventsWidget: the next 4 events from today to a year ahead, in the browser's zone. */
export function scheduledEventsQuery(now = new Date(), timeZone = guessTimeZone()): Record<string, QueryValue> {
  const end = new Date(now)
  end.setFullYear(end.getFullYear() + 1)
  return { limit: 4, 'filter[publishDate]': ymd(now), 'filter[publishDateEnd]': ymd(end), 'filter[timeZone]': timeZone }
}

export function guessTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** A converter media object (processId + host) from the cover upload. */
export type CoverMedia = Record<string, unknown>

export interface StreamForm {
  /** The editor's HTML. */
  description: string
  /** moment().format(): local time with its offset. */
  scheduledDate: string
  /** A newly uploaded cover. */
  cover: CoverMedia | null
  /** A cover is shown (uploaded now or kept from the stream). */
  hasPreview: boolean
  /** users/me needConsent: the consent was just given. */
  consent: boolean
}

/**
 * CreateEditScheduleStream.save: `mediaFiles` (new cover, or [] when an edited
 * stream's cover was removed), `description`, `scheduledDate`,
 * `isConsentAccepted` when OnlyFans asks for consent; a new stream also sends
 * `isShowTips: true`.
 */
export function streamBody(form: StreamForm, edit: { hadMedia: boolean } | null): Raw {
  const body: Raw = {}
  if (form.hasPreview && form.cover) body.mediaFiles = [form.cover]
  else if (edit?.hadMedia && !form.hasPreview) body.mediaFiles = []
  body.description = form.description
  body.scheduledDate = form.scheduledDate
  if (form.consent) body.isConsentAccepted = true
  return edit ? body : { ...body, isShowTips: true }
}

// ── Dates ─────────────────────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0')

/** 'YYYY-MM-DD' in local time. */
export function ymd(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** moment().format(): `YYYY-MM-DDTHH:mm:ss±HH:MM` in local time. */
export function localIso(d: Date): string {
  const off = -d.getTimezoneOffset()
  const sign = off >= 0 ? '+' : '-'
  const a = Math.abs(off)
  return `${ymd(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}${sign}${pad(Math.floor(a / 60))}:${pad(a % 60)}`
}

/** The date and time inputs' values for an instant. */
export function splitLocal(iso: string): { date: string; time: string } {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { date: '', time: '' }
  return { date: ymd(d), time: `${pad(d.getHours())}:${pad(d.getMinutes())}` }
}

/** Back from the inputs to an instant (null while incomplete). */
export function joinLocal(date: string, time: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  const t = /^(\d{2}):(\d{2})/.exec(time)
  if (!m || !t) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(t[1]), Number(t[2]), 0, 0)
  return Number.isNaN(d.getTime()) ? null : d
}

/** A new stream starts 10 minutes from now unless the URL names a date. */
export function defaultSchedule(scheduleDate: string, now = new Date()): string {
  if (scheduleDate && !Number.isNaN(new Date(scheduleDate).getTime())) return scheduleDate
  return localIso(new Date(now.getTime() + 10 * 60_000))
}

/** Validation rules notInPastDate (today is fine) and notInPastTime. */
export function dateProblems(at: Date | null, now = new Date()): { date: boolean; time: boolean } {
  if (!at) return { date: true, time: true }
  const future = at.getTime() > now.getTime()
  return { date: !future && at.toDateString() !== now.toDateString(), time: !future }
}

// ── Text ──────────────────────────────────────────────────────────────────

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", apos: "'", nbsp: ' ' }

/** OnlyFans HTML → the editor's plain text (paragraphs and <br> become lines). */
export function plainText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&(#\d+|#x[\da-f]+|\w+);/gi, (all, e: string) => {
      if (ENTITIES[e]) return ENTITIES[e]
      if (e.startsWith('#x')) return String.fromCodePoint(parseInt(e.slice(2), 16))
      if (e.startsWith('#')) return String.fromCodePoint(Number(e.slice(1)))
      return all
    })
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** What OnlyFans' editor (tiptap getHTML) sends: one <p> per line. */
export function editorHtml(text: string): string {
  return text
    .split('\n')
    .map((line) => (line ? `<p>${escapeHtml(line)}</p>` : '<p></p>'))
    .join('')
}

/** Rule minHtmlFree:4 — at least 4 characters of text. */
export const DESCRIPTION_MIN = 4
export const DESCRIPTION_MAX = 500

// ── Shapes ────────────────────────────────────────────────────────────────

export interface Page<T> {
  list: T[]
  hasMore: boolean
}

export function parsePage<T>(raw: unknown, one: (r: Raw) => T | null): Page<T> {
  const o = obj(raw)
  const list = arr(Array.isArray(raw) ? raw : o.list)
    .map(one)
    .filter((x): x is T => x !== null)
  return { list, hasMore: o.hasMore === true }
}

export interface PastStream {
  id: string
  startedAt: string
  viewsCount: number
  tipsCount: number
  /** media.files.thumb.url */
  thumb: string
  /** isMediaReady && media.isReady (else OnlyFans shows its processing spinner). */
  ready: boolean
}

/** A users/:id/streams entry; null when it has no media (the dashboard only lists those). */
export function parsePastStream(r: Raw): PastStream | null {
  const id = str(r.id)
  if (!id || !r.media || typeof r.media !== 'object') return null
  return {
    id,
    startedAt: str(r.startedAt),
    viewsCount: num(r.viewsCount),
    tipsCount: num(r.tipsCount),
    thumb: str(path(r, 'media', 'files', 'thumb', 'url')) || str(path(r, 'media', 'thumb')),
    ready: r.isMediaReady === true && path(r, 'media', 'isReady') === true,
  }
}

export interface ChartPoint {
  /** Seconds since the stream started. */
  point: number
  count: number
}

export type SeriesId = 'views' | 'likes' | 'comment' | 'tips'
export const SERIES: readonly SeriesId[] = ['views', 'likes', 'comment', 'tips']

export interface StreamStats {
  duration: string
  viewersCount: number
  likesCount: number
  commentsCount: number
  tipsCount: number
  tipSum: number
  series: Record<SeriesId, ChartPoint[]>
}

const points = (v: unknown): ChartPoint[] => arr(v).map((p) => ({ point: num(p.point), count: num(p.count) }))

/** streams/:id/stats?withChart=true */
export function parseStats(raw: unknown): StreamStats {
  const r = obj(raw)
  return {
    duration: str(r.duration),
    viewersCount: num(r.viewersCount),
    likesCount: num(r.likesCount),
    commentsCount: num(r.commentsCount),
    tipsCount: num(r.tipsCount),
    tipSum: num(r.tipSum),
    series: {
      views: points(r.viewersChartData),
      likes: points(r.likeChartData),
      comment: points(r.commentsChartData),
      tips: points(r.tipsChartData),
    },
  }
}

export interface Viewer {
  id: string
  name: string
  username: string
  avatar: string
}

/** A streams/:id/viewers entry (a user, or one wrapped in `user`). */
export function parseViewer(r: Raw): Viewer | null {
  const u = r.user && typeof r.user === 'object' ? obj(r.user) : r
  const id = str(u.id)
  if (!id) return null
  return { id, name: str(u.name), username: str(u.username), avatar: str(u.avatar) }
}

export interface ScheduledStream {
  id: string
  scheduledAt: string
  description: string
  rawDescription: string
  /** media.files.preview.url */
  preview: string
  hasMedia: boolean
}

/** streams/:id, read by the edit view. */
export function parseScheduledStream(raw: unknown): ScheduledStream | null {
  const r = obj(raw)
  const id = str(r.id)
  if (!id) return null
  return {
    id,
    scheduledAt: str(r.scheduledAt),
    description: str(r.description),
    rawDescription: str(r.rawDescription),
    preview: str(path(r, 'media', 'files', 'preview', 'url')),
    hasMedia: Boolean(r.media && typeof r.media === 'object'),
  }
}

export type EventType = 'post' | 'chat' | 'stream'

export interface ScheduleEvent {
  id: string
  type: EventType
  publishDateTime: string
  entityId: string
  /** Post/message text or stream description (HTML). */
  text: string
  price: number
  /** First 3 photo/video/gif thumbs (QueueAgenda's mediaList). */
  thumbs: string[]
  mediaPending: boolean
  isSavedForLater: boolean
}

const AGENDA_MEDIA = ['photo', 'video', 'gif']

/** A schedules list entry. */
export function parseScheduleEvent(r: Raw): ScheduleEvent | null {
  const id = str(r.id)
  if (!id) return null
  const type: EventType = r.type === 'chat' || r.type === 'stream' ? r.type : 'post'
  const e = obj(r.entity)
  const media = arr(e.media)
  return {
    id,
    type,
    publishDateTime: str(r.publishDateTime) || str(r.createdDateTime),
    entityId: str(e.id),
    text: str(e.description) || str(e.text) || str(e.rawDescription),
    price: num(e.price),
    thumbs: media
      .filter((m) => AGENDA_MEDIA.includes(str(m.type)))
      .slice(0, 3)
      .map((m) => str(path(m, 'files', 'thumb', 'url')) || str(m.thumb) || str(path(m, 'files', 'preview', 'url')))
      .filter(Boolean),
    mediaPending: e.isMediaReady === false,
    isSavedForLater: r.isSavedForLater === true,
  }
}

/**
 * ScheduledEventsWidget: streams starting within 30 minutes are shown on top
 * as "upcoming"; the rest grouped by local day.
 */
export function groupEvents(events: ScheduleEvent[], now = new Date()): { upcoming: ScheduleEvent[]; days: { date: string; events: ScheduleEvent[] }[] } {
  const four = events.slice(0, 4)
  const soon = (e: ScheduleEvent) => e.type === 'stream' && (new Date(e.publishDateTime).getTime() - now.getTime()) / 60_000 <= 30
  const days: { date: string; events: ScheduleEvent[] }[] = []
  for (const e of four.filter((x) => !soon(x))) {
    const d = new Date(e.publishDateTime)
    const key = Number.isNaN(d.getTime()) ? '' : ymd(d)
    const day = days.find((x) => x.date === key)
    if (day) day.events.push(e)
    else days.push({ date: key, events: [e] })
  }
  return { upcoming: four.filter(soon), days }
}

/** "Go live right now" is offered from 30 minutes before the start. */
export const canGoLiveSoon = (publishDateTime: string, now = new Date()) => (new Date(publishDateTime).getTime() - now.getTime()) / 60_000 <= 30

// ── users/me ──────────────────────────────────────────────────────────────

export interface StreamingMe {
  id: string
  canUploadMedia: boolean
  isScheduledStreamsAllowed: boolean
  canStreaming: boolean
  needConsent: boolean
  subscribePrice: number
  bundles: number
}

export function parseMe(raw: unknown): StreamingMe {
  const r = obj(raw)
  return {
    id: str(r.id),
    canUploadMedia: r.canUploadMedia === true,
    isScheduledStreamsAllowed: r.isScheduledStreamsAllowed === true,
    canStreaming: r.canStreaming === true,
    needConsent: r.needConsent === true,
    subscribePrice: num(r.subscribePrice),
    bundles: arr(r.subscriptionBundles).length,
  }
}

/** OnlyFans' price display ($9.99, $10). */
export function money(n: number): string {
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}`
}

// ── OBS (users/me/settings) ───────────────────────────────────────────────

export type ObsServerId = 'obs_agora' | 'mux_direct'

export interface ObsServer {
  id: ObsServerId
  server: string
  key: string
}

/** Settings > Streaming's two OBS servers: CloudBeta (RTMP) and Alternative (Mux direct). */
export function obsServers(settings: unknown): ObsServer[] {
  const s = obj(settings)
  return [
    { id: 'obs_agora', server: str(s.streamingRtmpServer), key: str(s.streamingRtmpKey) },
    { id: 'mux_direct', server: str(s.streamingMuxServer), key: str(s.streamingMuxDirectKey) },
  ]
}

// ── Chart ─────────────────────────────────────────────────────────────────

/** "m:ss" / "h:mm:ss" for a point in seconds (labels under the stats chart). */
export function pointLabel(sec: number, max: number): string {
  const s = Math.max(0, Math.round(sec))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const r = s % 60
  return max >= 3600 ? `${h}:${pad(m)}:${pad(r)}` : `${m}:${pad(r)}`
}

/** SVG path for one series, scaled to its own maximum like OnlyFans' multi-line chart. */
export function seriesPath(pts: ChartPoint[], maxPoint: number, w: number, h: number): string {
  if (!pts.length) return ''
  const list = pts.length === 1 ? [{ point: 0, count: 0 }, pts[0]!] : pts
  const top = Math.max(1, ...list.map((p) => p.count))
  const span = Math.max(1, maxPoint)
  return list
    .map((p, i) => `${i ? 'L' : 'M'}${((p.point / span) * w).toFixed(1)},${(h - (p.count / top) * (h - 4) - 2).toFixed(1)}`)
    .join(' ')
}
